import { PRINT_OPTIONS_CHOICE_CUSTOMER, PRINT_OPTIONS_CHOICE_DESIGNER } from './types';
import type { PrintOptionsChoice } from './types';

/**
 * The assistant-side print-options question payload.
 *
 * Unlike style-tiles, this carries **no options**. The option list is compatibility-filtered —
 * choosing a size changes which finishes are selectable — so it is resolved by querying
 * `printProductConfigurator` and re-resolved on every selection. The assistant only says "ask
 * this question", plus anything it already knows about the answer.
 */
export type PrintOptionsQuestion = {
    title?: string;
    /** Pre-selects the step-1 choice when the assistant already knows the customer's intent. */
    preselectedChoice?: PrintOptionsChoice;
    /**
     * Pre-selects specific option values for step 2. Keys are option attribute keys (e.g.
     * "Shape"), values are value keys (e.g. "Square"). Implies a CUSTOMER choice and starts the
     * wizard on step 2. Unrecognised keys and values are ignored by the configurator.
     */
    preselectedSelections?: Record<string, string>;
};

function isRecord(value: unknown): value is Record<string, unknown> {
    return typeof value === 'object' && value !== null && !Array.isArray(value);
}

function isStringRecord(value: unknown): value is Record<string, string> {
    return isRecord(value) && Object.values(value).every((entry) => typeof entry === 'string');
}

/**
 * Parses the assistant's print-options question payload.
 *
 * Lenient by design, and more so than the pilot: the pilot's `validate` also read `mpvId` and
 * `tenant` from this payload and defaulted them to `''`, then had them overwritten by the host
 * before render. Those fields are ignored here — the route is the authority on the product (see
 * `GenerativeUiDeps`), so accepting them from the assistant would only re-create a value that
 * is discarded.
 *
 * Every field is optional, so an empty object is valid: the question is answerable from the
 * step-1 choice alone. Only a non-record, or a field of the wrong type, fails.
 *
 * @param data - The assistant's `component.data`
 * @returns A validated `PrintOptionsQuestion`, or `null` if validation fails
 */
export function parsePrintOptionsQuestion(data: unknown): PrintOptionsQuestion | null {
    if (!isRecord(data)) {
        return null;
    }

    const { title, preselectedChoice, preselectedSelections } = data;

    if (title !== undefined && typeof title !== 'string') {
        return null;
    }

    // Unlike `title`, an unrecognised choice is dropped rather than failing the parse. It is a
    // hint the assistant volunteered, not part of the question: discarding it costs the customer
    // one click, whereas rejecting the payload would leave them with no selector at all.
    const choice =
        preselectedChoice === PRINT_OPTIONS_CHOICE_CUSTOMER ||
        preselectedChoice === PRINT_OPTIONS_CHOICE_DESIGNER
            ? preselectedChoice
            : undefined;

    const selections = isStringRecord(preselectedSelections) ? preselectedSelections : undefined;

    return {
        ...(title !== undefined ? { title } : {}),
        ...(choice !== undefined ? { preselectedChoice: choice } : {}),
        ...(selections !== undefined ? { preselectedSelections: { ...selections } } : {}),
    };
}
